/* External */
import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

/* Ott-common */
import LiveCardCommon from '@dtvgo/live-card';
import Icon from '@dtvgo/icon';
import { formatDate, isTodayOrTomorrowDay } from '~/utils/dateUtils';

/* Components */
import LiveProgressBar from '../LiveProgressBar';
import LiveStartTime from '../LiveStartTime';
import LiveInteractiveTag from '../LiveInteractiveTag';

/* Utils */
import { ImageRatio, TIME_FORMAT_FOR_SCHEDULES } from '../../utils/constants';
import { Schedule } from '../../storage/models';
import { useSubtitleLiveCard } from './utils';
import { useTitle, isInteractivityAllowed } from '../../utils/metaDataUtils';
import { useUserAccount } from '../../providers/UserProvider';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import useLiveContentTexts from '../../hooks/useLiveContentTexts';
import useChannel from '../../hooks/useChannel';
import useContent from '../../hooks/useContent';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';
import { CardWidth, CardType } from '../../utils/cardsUtils';

/* Hooks */
import useImageCard from '../../hooks/useImageCard';

/* Styles */
import './LiveCard.scss';

const IMG_WIDTH = CardWidth[CardType.LIVE];
const IMG_DIMENSION = ImageRatio.SIXTEEN_BY_NINE;

function LiveCard({
  className = '',
  focused,
  item: schedule,
  onMouseDown,
  onMouseEnter,
}) {
  const {
    content: contentId,
    startTime,
    endTime,
    channelId,
  } = schedule || {};

  const content = useContent(contentId);

  const { images } = content || schedule;

  const TimeLiveContentTexts = useLiveContentTexts();

  const { timeZone } = useIntl();

  const title = useTitle(content, schedule);
  const subtitle = useSubtitleLiveCard(content || schedule);

  const showLockIcon = useLockIcon(content || schedule);
  const isContentBlockedByParentalControl = parentalControlBlock(content || schedule);

  const { imageUrl } = useImageCard({
    images,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  const startDateFormatted = useMemo(() => {
    if (startTime && TimeLiveContentTexts) {
      const day = isTodayOrTomorrowDay(startTime, TimeLiveContentTexts);
      return `${day} ${formatDate(startTime, TIME_FORMAT_FOR_SCHEDULES, timeZone)}`;
    }
    return '';
  }, [TimeLiveContentTexts, startTime, timeZone]);

  const channel = useChannel(channelId);
  const { uid: userId, iso2Code } = useUserAccount() || {};
  const isInteractive = isInteractivityAllowed(schedule, iso2Code, userId);

  return (
    <LiveCardCommon
      className={
        `dtv-html5-c-live-card ${focused ? 'dtv-html5-c-live-card--focused' : ''
        } ${className}`
      }
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <LiveCardCommon.Image
        className="dtv-html5-c-live-card__image"
        imageUrl={imageUrl}
        imageAlt={title || ''}
      >
        {isInteractive && (
          <div className="dtv-html5-c-live-card__interactivity-tag">
            <LiveInteractiveTag />
          </div>
        )}
        <LiveProgressBar
          className="dtv-html5-c-live-card__progress"
          startDate={startTime}
          endDate={endTime}
          focused={focused}
        />
      </LiveCardCommon.Image>
      {showLockIcon && <LiveCardCommon.Lock />}
      {(isContentBlockedByParentalControl && !showLockIcon) && (
        <div className="dtv-html5-c-live-card__parental-control-block">
          <Icon name="parental-block" className="dtv-html5-c-live-card__parental-control-icon" />
        </div>
      )}
      <LiveCardCommon.Info className="dtv-html5-c-live-card__metadata">
        <div>
          <LiveCardCommon.Info.Title className="dtv-html5-c-live-card__title">{title || ''}</LiveCardCommon.Info.Title>
          <LiveCardCommon.Info.Subtitle className="dtv-html5-c-live-card__subtitle">
            <p>{subtitle}</p>
          </LiveCardCommon.Info.Subtitle>
        </div>
        <div className="dtv-html5-c-live-card__live-icon-content">
          <LiveStartTime
            startDate={startTime}
            endDate={endTime}
            className="dtv-html5-c-live-card__live-icon"
            showStartInText={false}
          >
            <span className="dtv-html5-c-live-card__day-and-start-date">{startDateFormatted}</span>
          </LiveStartTime>
          <span className="dtv-html5-c-live-card__station">{channel?.title || schedule?.channelName || ''}</span>
        </div>
      </LiveCardCommon.Info>
    </LiveCardCommon>
  );
}

LiveCard.propTypes = {
  className: PropTypes.string,
  focused: PropTypes.bool.isRequired,
  item: PropTypes.instanceOf(Schedule).isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

export default memo(withInteractiveFocusable()(memo(LiveCard)));
